import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import Inventory2Icon from "@material-ui/icons/LocalMall";
import WarningIcon from "@material-ui/icons/Warning";
import BlockIcon from "@material-ui/icons/Block";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ImageIcon from "@material-ui/icons/Image";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { DetailField, CopyableField, ImageStrip, assetUrl, readableError } from "../shopFields";
import "../superShops.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const PRODUCT_FILTERS = `
  shop: $shop
  title: $title
  shopMenu: $shopMenu
  priceFrom: $priceFrom
  price: $price
  stockFrom: $stockFrom
  stock: $stock
`;

const PRODUCT_VARIABLES = `
  $limit: Int
  $skip: Int
  $shop: ID
  $title: String
  $shopMenu: String
  $priceFrom: Float
  $price: Float
  $stockFrom: Int
  $stock: Int
  $priceSort: Int
  $stockSort: Int
  $createdAtSort: Int
`;

const GET_PRODUCTS = gql`
  query getProductsByAdmin(${PRODUCT_VARIABLES}) {
    getProductsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${PRODUCT_FILTERS} }
      sort: { price: $priceSort, stock: $stockSort, createdAt: $createdAtSort }
    ) {
      _id
      title
      description
      price
      stock
      photoUrl
      shopMenuName
      createdAt
      attributes {
        attributeGroup {
          _id
          name
        }
        att {
          _id
          name
        }
      }
    }
    getProductsByAdminCount(filters: { ${PRODUCT_FILTERS} })
  }
`;

// Stock health across the whole shop, not just the page on screen — the
// reason to open this list is usually to find what has run out.
const GET_STOCK_COUNTS = gql`
  query getShopStockCounts($shop: ID) {
    total: getProductsByAdminCount(filters: { shop: $shop })
    outOfStock: getProductsByAdminCount(filters: { shop: $shop, stock: 0 })
  }
`;

const GET_SHOP_MENU = gql`
  query getShopMenuForProducts($id: ID!) {
    getShopByAdmin(id: $id) {
      _id
      shopMenu {
        _id
        subMenus {
          _id
          name
        }
      }
    }
  }
`;

const DELETE_PRODUCTS = gql`
  mutation removeProductByAdmin($idSet: [ID!]!) {
    removeProductByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STOCK_OPTIONS = [
  { value: "", label: "Any stock" },
  { value: "out", label: "Out of stock" },
  { value: "low", label: "Low (1-5)" },
  { value: "in", label: "In stock" },
];
const STOCK_LABELS = STOCK_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "priceHigh", label: "Price high-low" },
  { value: "priceLow", label: "Price low-high" },
  { value: "stockLow", label: "Stock low-high" },
];

const LOW_STOCK_THRESHOLD = 5;
const FILTER_DEBOUNCE_MS = 350;

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const stockState = (stock) => {
  if (stock === null || stock === undefined) return { tone: "neutral", label: "Not tracked" };
  if (stock <= 0) return { tone: "danger", label: "Out of stock" };
  if (stock <= LOW_STOCK_THRESHOLD) return { tone: "warning", label: `${stock} left` };
  return { tone: "success", label: `${stock} in stock` };
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const SuperProducts = ({ id: shopId, shopName, shopTitle, wording }) => {
  const words = wording || { products: "Products", product: "Product", menuItem: "Category" };

  const [searchTerm, setSearchTerm] = useState("");
  const [menuFilter, setMenuFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [productDetail, setProductDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    shop: shopId,
    createdAtSort: -1,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setVariables((v) => ({
        ...v,
        skip: 0,
        shop: shopId,
        title: searchTerm.trim() || undefined,
        shopMenu: menuFilter || undefined,
        priceFrom: priceFrom ? parseFloat(priceFrom) : undefined,
        price: priceTo ? parseFloat(priceTo) : undefined,
        // The backend exposes stock as an exact match plus a floor, so
        // "out of stock" is stock: 0 and "low" is a floor of 1 with a
        // ceiling applied on the page.
        stock: stockFilter === "out" ? 0 : undefined,
        stockFrom: stockFilter === "in" || stockFilter === "low" ? 1 : undefined,
        priceSort: sortOption === "priceHigh" ? -1 : sortOption === "priceLow" ? 1 : undefined,
        stockSort: sortOption === "stockLow" ? 1 : undefined,
        createdAtSort:
          sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [shopId, searchTerm, menuFilter, stockFilter, priceFrom, priceTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_PRODUCTS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_STOCK_COUNTS, {
    variables: { shop: shopId },
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const { data: menuData } = useQuery(GET_SHOP_MENU, {
    variables: { id: shopId },
    errorPolicy: "all",
  });
  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_PRODUCTS, {
    fetchPolicy: "network-only",
  });
  const [deleteProducts] = useMutation(DELETE_PRODUCTS);

  const allProducts = (data && data.getProductsByAdmin) || [];
  const serverCount = (data && data.getProductsByAdminCount) || 0;
  const counts = countsData || {};

  // "Low" needs a ceiling the filter input cannot express, so it is
  // finished on the page and the toolbar says how many rows that left.
  const products =
    stockFilter === "low"
      ? allProducts.filter((p) => p.stock > 0 && p.stock <= LOW_STOCK_THRESHOLD)
      : allProducts;

  const menuOptions = (() => {
    const menu = menuData && menuData.getShopByAdmin && menuData.getShopByAdmin.shopMenu;
    return ((menu && menu.subMenus) || []).map((s) => ({ value: s.name, label: s.name }));
  })();

  const lowOnPage = allProducts.filter((p) => p.stock > 0 && p.stock <= LOW_STOCK_THRESHOLD).length;

  /* ---------------- selection + delete ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = products.map((p) => p._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    products.length > 0 && selectedIds.length === products.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  const runDelete = () => {
    const targets = deleteTarget || [];
    setDeleting(true);
    setActionError("");
    deleteProducts({ variables: { idSet: targets.map((p) => p._id) } })
      .then(() => {
        setDeleting(false);
        setDeleteTarget(null);
        setProductDetail(null);
        setSelectedIds([]);
        refetch();
        refetchCounts();
      })
      .catch((err) => {
        setDeleting(false);
        setDeleteTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (searchTerm.trim()) activeFilterChips.push({ id: "search", label: `Title: ${searchTerm}` });
  if (menuFilter) activeFilterChips.push({ id: "menu", label: `${words.menuItem}: ${menuFilter}` });
  if (stockFilter) activeFilterChips.push({ id: "stock", label: STOCK_LABELS[stockFilter] });
  if (priceFrom || priceTo) {
    activeFilterChips.push({ id: "price", label: `Price: $${priceFrom || "0"} to $${priceTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "menu") setMenuFilter("");
    if (id === "stock") setStockFilter("");
    if (id === "price") {
      setPriceFrom("");
      setPriceTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setMenuFilter("");
    setStockFilter("");
    setPriceFrom("");
    setPriceTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [["Title", words.menuItem, "Price", "Stock", "Options", "Added"]];
    (exportData.getProductsByAdmin || []).forEach((p) => {
      rows.push([
        p.title || "-",
        p.shopMenuName || "-",
        Number(p.price || 0).toFixed(2),
        p.stock === null || p.stock === undefined ? "-" : p.stock,
        (p.attributes || [])
          .map((a) => `${a.attributeGroup ? a.attributeGroup.name : ""}: ${a.att ? a.att.name : ""}`)
          .join(", ") || "-",
        p.createdAt ? moment(p.createdAt).format("MM/DD/YYYY") : "-",
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), words.products);
    XLSX.writeFile(wb, `${(shopTitle || "Shop").replace(/\s+/g, "-")}-${words.products}.xlsx`);
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (serverCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: serverCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superShopsPage superShopProductsPage">
        <main className="main">
          <PageHeader title={words.products} />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedProducts = products.filter((p) => selectedIds.includes(p._id));

  return (
    <div className="page superShopsPage superShopProductsPage">
      <main className="main">
        <PageHeader
          title={words.products}
          count={serverCount}
          description={
            shopTitle
              ? `Everything ${shopTitle} sells, and what is left in stock.`
              : "Everything this shop sells, and what is left in stock."
          }
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${serverCount} item${serverCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={serverCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard icon={<Inventory2Icon />} title="Matching items" value={serverCount} />
          <MetricCard icon={<Inventory2Icon />} title={`All ${words.products.toLowerCase()}`} value={counts.total || 0} />
          <MetricCard
            icon={<BlockIcon />}
            title="Out of stock"
            value={counts.outOfStock || 0}
            tone={counts.outOfStock > 0 ? "danger" : "default"}
          />
          <MetricCard
            icon={<WarningIcon />}
            title="Low stock"
            value={lowOnPage}
            hint="On this page"
            tone={lowOnPage > 0 ? "warning" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search ${words.products.toLowerCase()} by title...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={menuFilter ? `${words.menuItem}: ${menuFilter}` : words.menuItem}
              options={[{ value: "", label: `All ${words.menuItem.toLowerCase()}s` }, ...menuOptions]}
              value={menuFilter}
              onChange={(e) => setMenuFilter(e.target.value)}
            />
            <FilterDropdown
              label={stockFilter ? STOCK_LABELS[stockFilter] : "Stock"}
              options={STOCK_OPTIONS}
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
            />
            <NumberRangeFilter
              label="Price"
              fromValue={priceFrom}
              toValue={priceTo}
              onFromChange={(e) => setPriceFrom(e.target.value)}
              onToChange={(e) => setPriceTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="dangerQuiet"
              size="sm"
              icon={<DeleteIcon fontSize="small" />}
              onClick={() => setDeleteTarget(selectedProducts)}
            >
              Delete selected
            </Button>
          }
          leftContent={
            stockFilter === "low" ? (
              <span className="superShopPill">
                {products.length} of {allProducts.length} on this page
              </span>
            ) : null
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superShopsSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every item on this page" }}
                />
              </div>
              <div className="tableHeaderCell">{words.product}</div>
              <div className="tableHeaderCell">{words.menuItem}</div>
              <div className="tableHeaderCell">Price</div>
              <div className="tableHeaderCell">Stock</div>
              <div className="tableHeaderCell">Added</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {products.length === 0 ? (
              <EmptyState
                icon={<Inventory2Icon />}
                title={activeFilterChips.length === 0 ? `No ${words.products.toLowerCase()} yet` : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? `Add something to a ${words.menuItem.toLowerCase()} to see it here.`
                    : "Nothing matches these filters. Try widening or clearing them."
                }
              />
            ) : (
              products.map((product) => {
                const photo = assetUrl((product.photoUrl || [])[0]);
                const stock = stockState(product.stock);
                return (
                  <div className="row" key={product._id}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(product._id)}
                      onChange={() => toggleSelectOne(product._id)}
                      inputProps={{ "aria-label": `Select ${product.title}` }}
                    />

                    <div className="superShopCell" data-label={words.product}>
                      <span className="superShopLogo">
                        {photo ? <img src={photo} alt="" /> : <ImageIcon fontSize="small" />}
                      </span>
                      <span className="superShopText">
                        <span className="superShopName">{product.title}</span>
                        {product.description && (
                          <span className="superShopMeta">{product.description}</span>
                        )}
                      </span>
                    </div>

                    <span className="superShopTag" data-label={words.menuItem}>
                      {product.shopMenuName || "Unassigned"}
                    </span>

                    <span className="superShopAmount" data-label="Price">
                      {money(product.price)}
                    </span>

                    <span data-label="Stock">
                      <StatusBadge status={stock.tone} label={stock.label} />
                    </span>

                    <span className="updated" data-label="Added">
                      <strong>{moment(product.createdAt).format("MMM D, YYYY")}</strong>
                      <span>{moment(product.createdAt).format("hh:mm A")}</span>
                    </span>

                    <div className="superShopActions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() =>
                          (window.location.href = `${endPoint}/superAdmin/${shopName}/${shopId}/editProduct/${product._id}`)
                        }
                        title={`Edit ${words.product.toLowerCase()}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setProductDetail(product)}
                        title="Details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {serverCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={serverCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(productDetail)}
        onClose={() => setProductDetail(null)}
        title={`${words.product} details`}
        subtitle={productDetail ? productDetail.title : ""}
      >
        {productDetail && (
          <div className="superShopDetails superShopDetailsNarrow">
            <div className="superShopDetailsTop">
              <StatusBadge
                status={stockState(productDetail.stock).tone}
                label={stockState(productDetail.stock).label}
              />
              <span className="superShopDetailsHeadline">{money(productDetail.price)}</span>
            </div>
            <div className="superShopGrid">
              <CopyableField label={`${words.product} ID`} value={productDetail._id} />
              <DetailField label={words.menuItem} value={productDetail.shopMenuName} />
              <DetailField
                label="Added"
                value={moment(productDetail.createdAt).format("MMM D, YYYY [at] hh:mm A")}
              />
              <DetailField label="Description" value={productDetail.description} wide />
              {(productDetail.attributes || []).length > 0 && (
                <DetailField
                  label="Options"
                  wide
                  value={
                    <span className="superShopImages">
                      {(productDetail.attributes || []).map((a, index) => (
                        <span className="superShopTag" key={index}>
                          {a.attributeGroup ? `${a.attributeGroup.name}: ` : ""}
                          {a.att ? a.att.name : ""}
                        </span>
                      ))}
                    </span>
                  }
                />
              )}
            </div>
            <ImageStrip
              label="Photos"
              urls={(productDetail.photoUrl || []).map(assetUrl)}
              wide
            />
            <div className="superShopFooter">
              <Button variant="dangerQuiet" onClick={() => setDeleteTarget([productDetail])}>
                Delete
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  (window.location.href = `${endPoint}/superAdmin/${shopName}/${shopId}/editProduct/${productDetail._id}`)
                }
              >
                Edit
              </Button>
            </div>
          </div>
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        title={deleteTarget && deleteTarget.length > 1 ? `Delete ${words.products.toLowerCase()}` : `Delete ${words.product.toLowerCase()}`}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null), disabled: deleting },
          { label: "Delete", onClick: runDelete, variant: "danger", loading: deleting },
        ]}
      >
        <div className="superShopConfirm">
          {deleteTarget && deleteTarget.length > 1
            ? `${deleteTarget.length} items will be removed from the shop. Items already in a customer's cart cannot be deleted.`
            : "This item will be removed from the shop. Items already in a customer's cart cannot be deleted."}
        </div>
      </DialogShell>
    </div>
  );
};

export default SuperProducts;
